import { Inject, Injectable, Optional } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { EMPTY, merge, Observable, Subscription, tap } from 'rxjs';
import { CalcDeps, CalcRule } from '@marketiq/calc-core';
import { ColumnIdMap } from '../adapters/column-id-map';
import { FormGroupCalculationContext } from '../adapters/form-group.adapter';
import { AUTO_CALC_CONFIGS_TOKEN, AutoCalculationConfiguration } from '../auto-calculation.token';

/** Output writes are silent so a rule's result never re-triggers another listener (loop guard). */
const SILENT = { emitEvent: false } as const;

/**
 * Generic listener factory: subscribe to a rule's input controls and re-run the rule whenever any
 * of them changes, writing the rule's outputs back into the FormGroup silently.
 *
 * This single engine replaces the per-field RxJS plumbing that was hand-written across ~15 listener
 * files — the math now lives in `@marketiq/calc-core`, and this drives it. Returns `EMPTY` when none of
 * the rule's inputs resolve to a control in this form.
 */
export function bindRule(
  fg: FormGroup,
  rule: CalcRule,
  columnIdMap: ColumnIdMap,
  deps?: CalcDeps
): Observable<unknown> {
  const controls = rule.inputs
    .map(key => {
      const id = columnIdMap.idFor(key);
      return id == null ? null : fg.get(String(id));
    })
    .filter((control): control is NonNullable<typeof control> => !!control);

  if (controls.length === 0) {
    return EMPTY;
  }

  return merge(...controls.map(control => control.valueChanges)).pipe(
    tap(() => {
      const ctx = new FormGroupCalculationContext(fg, columnIdMap, SILENT);
      rule.run(ctx, deps);
    })
  );
}

/** Bind a set of rules; skips rules whose inputs aren't present in the form. */
export function bindRules(
  fg: FormGroup,
  rules: readonly CalcRule[],
  columnIdMap: ColumnIdMap,
  deps?: CalcDeps
): Observable<unknown>[] {
  return rules.map(rule => bindRule(fg, rule, columnIdMap, deps)).filter(stream => stream !== EMPTY);
}

/**
 * Attaches calc-core rules to a host `FormGroup`, reading the host bindings (column map, rounding,
 * locale basis) from {@link AUTO_CALC_CONFIGS_TOKEN}. Mirrors the role of the old per-entity listener
 * registration in `sections-override.config.ts`, but generically.
 */
@Injectable({ providedIn: 'root' })
export class AutoCalcOrchestratorService {
  constructor(
    @Optional() @Inject(AUTO_CALC_CONFIGS_TOKEN) private readonly config?: AutoCalculationConfiguration
  ) {}

  /** Subscribe `rules` to `fg`. Returns a Subscription the caller tears down with the form. */
  attach(fg: FormGroup, rules: readonly CalcRule[]): Subscription {
    const sub = new Subscription();
    const columnIdMap = this.config?.columnIdMap;
    if (!columnIdMap) {
      return sub; // no host config → no-op (e.g. unit tests using bindRules directly)
    }
    for (const stream of bindRules(fg, rules, columnIdMap, this.calcDeps())) {
      sub.add(stream.subscribe());
    }
    return sub;
  }

  /** The ambient inputs passed to every rule, sourced from the host config. */
  calcDeps(): CalcDeps {
    return {
      round: this.config?.round,
      leaseTermBasis: this.config?.leaseTermBasis,
    };
  }
}
